import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Select,
  Badge,
  Pagination,
  LoadingSkeleton,
  Input,
  Modal,
  Textarea,
  StatCard,
  StatsGrid,
} from '@nexora/design-system';
import { useAuth } from '../../shared/auth/AuthContext';
import {
  useSupportTickets,
  useSupportTicket,
  useCreateTicket,
  useRespondTicket,
  useCloseTicket,
  useCsatTicket,
} from './useSupportTickets';
import type {
  CreateTicketInput,
  TicketAuthorType,
  TicketSeverity,
} from './useSupportTickets';
import {
  ticketStatusMeta,
  ticketSeverityMeta,
  ticketSlaLimit,
  summarizeTickets,
  filterTickets,
  SLA_HOURS,
} from './supportTicketView';
import { formatDate, formatDateTime } from '../../shared/utils/format';

/* ── Constantes ── */

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'OPEN', label: 'Aberto' },
  { value: 'IN_PROGRESS', label: 'Em andamento' },
  { value: 'WAITING_CUSTOMER', label: 'Aguard. cliente' },
  { value: 'RESOLVED', label: 'Resolvido' },
  { value: 'CLOSED', label: 'Fechado' },
];

const SEVERITY_ORDER: TicketSeverity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

/* ── Helpers de formato ── */

function formatCsat(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(1)} / 5`;
}

/* ── Ícones (stroke, 22px) ── */

function iconProps() {
  return {
    width: 22,
    height: 22,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  } as const;
}

function InboxIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.5 5.1 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.5-6.9A2 2 0 0 0 16.7 4H7.3a2 2 0 0 0-1.8 1.1Z" />
    </svg>
  );
}

function SirenIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M12 3 2 20h20L12 3Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ClockIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

function StarIcon(): JSX.Element {
  return (
    <svg {...iconProps()}>
      <path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1L12 2Z" />
    </svg>
  );
}

/* ── Página ── */

export function SupportTicketsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data, isLoading, error, refetch } = useSupportTickets(tenantId, {
    status: statusFilter,
    severity: severityFilter,
    page,
    limit: PAGE_SIZE,
  });

  const createMutation = useCreateTicket(tenantId);

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const items = useMemo(() => data?.data ?? [], [data]);
  const summary = useMemo(() => summarizeTickets(items), [items]);
  const filtered = useMemo(() => filterTickets(items, search), [items, search]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const severityTotal = SEVERITY_ORDER.reduce((sum, sev) => sum + summary.bySeverity[sev], 0);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleSeverityBar = useCallback((severity: string) => {
    setSeverityFilter(severity);
    setPage(1);
  }, []);

  const handleCreate = useCallback(
    async (input: CreateTicketInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Chamado criado com sucesso.', 'success');
        setCreateOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao criar chamado.', 'error');
      }
    },
    [createMutation, showToast],
  );

  return (
    <div className="sup-page">
      {/* Header executivo */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="sup-page__eyebrow">Suporte · Central de chamados</p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Chamados de Suporte
            {data && <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">({total})</span>}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            SLA por severidade (2h–72h), com histórico de mensagens por chamado.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + Novo chamado
          </Button>
        </div>
      </div>

      {/* KPIs derivados da lista (agregação client-side, sem refetch) */}
      {isLoading ? (
        <StatsGrid>
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
        </StatsGrid>
      ) : (
        <StatsGrid>
          <StatCard
            variant="primary"
            icon={<InboxIcon />}
            value={String(summary.open)}
            label="Chamados em aberto"
            trend={{ value: `${summary.count} nesta página · derivado`, direction: 'neutral' }}
          />
          <StatCard
            variant="error"
            icon={<SirenIcon />}
            value={String(summary.criticalHigh)}
            label="Críticos + altos"
            trend={{
              value: `${summary.bySeverity.CRITICAL} críticos · página atual`,
              direction: summary.criticalHigh > 0 ? 'down' : 'up',
            }}
          />
          <StatCard
            variant="warning"
            icon={<ClockIcon />}
            value={String(summary.slaBreached)}
            label="SLA estourado"
            trend={{ value: 'Página atual · estimado', direction: summary.slaBreached > 0 ? 'down' : 'up' }}
          />
          <StatCard
            variant="success"
            icon={<StarIcon />}
            value={formatCsat(summary.csatAverage)}
            label={`CSAT médio (${summary.csatCount} aval.)`}
            trend={{ value: 'Página atual · derivado', direction: 'neutral' }}
          />
        </StatsGrid>
      )}

      {/* Barra de severidade (proxy de lane de triagem; filtra no servidor) */}
      <Card padding="1rem" variant="outlined">
        <div
          className="sup-severity-bar"
          role="group"
          aria-label="Filtrar chamados por severidade (aplica filtro no servidor)"
        >
          <button
            type="button"
            className={`sup-severity-chip${severityFilter === 'ALL' ? ' active' : ''}`}
            aria-pressed={severityFilter === 'ALL'}
            onClick={() => handleSeverityBar('ALL')}
          >
            Todas ({severityTotal})
          </button>
          {SEVERITY_ORDER.map((sev) => {
            const meta = ticketSeverityMeta(sev);
            return (
              <button
                key={sev}
                type="button"
                className={`sup-severity-chip sup-severity-chip--${meta.variant}${severityFilter === sev ? ' active' : ''}`}
                aria-pressed={severityFilter === sev}
                onClick={() => handleSeverityBar(sev)}
                title={`SLA contratual: ${SLA_HOURS[sev]}h`}
              >
                {meta.label} ({summary.bySeverity[sev]})
              </button>
            );
          })}
        </div>
        <div className="sup-page__filter-row">
          <div className="sup-page__filter-field">
            <Select
              label="Filtrar por status"
              value={statusFilter}
              onChange={handleStatusChange}
              aria-label="Filtrar por status (aplica filtro no servidor)"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="sup-page__filter-field sup-page__filter-field--grow">
            <Input
              label="Buscar na página"
              placeholder="Número, assunto ou descrição..."
              value={search}
              onChange={handleSearchChange}
              aria-label="Buscar na página atual por número, assunto ou descrição"
            />
          </div>
        </div>
      </Card>

      {/* Conteúdo */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {total ?? 0} {(total ?? 0) === 1 ? 'chamado' : 'chamados'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Chamados</h3>
        </CardHeader>
        {isLoading && (
          <div className="sup-page__loading" aria-busy="true" aria-live="polite">
            <LoadingSkeleton width="100%" height="48px" />
            <LoadingSkeleton width="100%" height="48px" />
            <LoadingSkeleton width="100%" height="48px" />
            <span className="sup-page__loading-text">Carregando chamados...</span>
          </div>
        )}

        {error && !isLoading && (
          <div className="sup-page__error" role="alert">
            <p className="sup-page__error-title">Erro ao carregar chamados de suporte</p>
            <p className="sup-page__error-detail">{(error as Error).message}</p>
            <Button
              variant="secondary"
              onClick={() => {
                void refetch();
              }}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="sup-page__empty" role="status">
            <p className="sup-page__empty-text">
              {search
                ? 'Nenhum chamado nesta página corresponde à busca.'
                : 'Nenhum chamado encontrado.'}
            </p>
            {!search && (
              <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)}>
                Abrir primeiro chamado
              </Button>
            )}
          </div>
        )}

        {!isLoading && !error && filtered.length > 0 && (
          <div className="sup-page__table-wrap">
            <table className="sup-page__table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Assunto</th>
                  <th scope="col">Severidade</th>
                  <th scope="col">Status</th>
                  <th scope="col">SLA limite</th>
                  <th scope="col">Criado por</th>
                  <th scope="col">Data</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const sev = ticketSeverityMeta(t.severity);
                  const st = ticketStatusMeta(t.status);
                  const sla = ticketSlaLimit(t);
                  return (
                    <tr key={t.id} className="sup-row">
                      <td className="sup-cell--number">{t.number}</td>
                      <td className="sup-cell--subject" title={t.subject}>
                        {t.subject}
                      </td>
                      <td>
                        <Badge variant={sev.variant} size="sm" dot>
                          {sev.label}
                        </Badge>
                      </td>
                      <td>
                        <Badge variant={st.variant} size="sm" dot>
                          {st.label}
                        </Badge>
                      </td>
                      <td
                        className="sup-cell--date"
                        title={sla.limit ? (sla.estimated ? 'Limite estimado (createdAt + SLA da severidade)' : 'Limite informado pelo servidor') : 'Sem limite calculável'}
                      >
                        {sla.limit ? formatDateTime(sla.limit.toISOString()) : '—'}
                        {sla.limit && sla.estimated && <span className="sup-cell--est"> *</span>}
                      </td>
                      <td>{t.createdBy ?? '—'}</td>
                      <td className="sup-cell--date">{formatDate(t.createdAt)}</td>
                      <td className="sup-cell--actions">
                        <div className="sup-actions">
                          <Button variant="ghost" size="sm" onClick={() => setDetailId(t.id)}>
                            Detalhes
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!isLoading && !error && (
          <div className="sup-page__footer">
            <span className="sup-page__footer-text">
              {search
                ? `Mostrando ${filtered.length} de ${items.length} (página atual · busca client-side)`
                : `Mostrando ${items.length} de ${total} · página ${page} de ${totalPages}`}
              {' · * SLA estimado'}
            </span>
            {totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
            )}
          </div>
        )}
      </Card>

      {/* Drawer: detalhe + timeline + transições */}
      <TicketDetailDrawer
        tenantId={tenantId}
        ticketId={detailId}
        onClose={() => setDetailId(null)}
        onToast={showToast}
      />

      {/* Modal criar */}
      <CreateTicketModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        submitting={createMutation.isPending}
      />

      {/* Toast */}
      {toastMessage && (
        <div role="alert" className={`sup-toast sup-toast--${toastVariant}`}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar notificação" className="sup-toast__close">
            &times;
          </button>
        </div>
      )}

      <style>{`
        .sup-page { padding: 1.5rem; display: flex; flex-direction: column; gap: 1rem; }
        .sup-page__eyebrow { margin: 0 0 0.25rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: #002045; font-weight: 700; }
        .sup-severity-bar { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1rem; }
        .sup-severity-chip { border: 1px solid #e0e3e5; background: #ffffff; border-radius: 9999px; padding: 0.4rem 0.9rem; font-size: 0.8125rem; font-weight: 600; color: #43474e; cursor: pointer; }
        .sup-severity-chip:hover { background: #f2f4f6; }
        .sup-severity-chip.active { background: #191c1e; border-color: #191c1e; color: #ffffff; }
        .sup-severity-chip:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .sup-severity-chip--error.active { background: #ba1a1a; border-color: #ba1a1a; }
        .sup-severity-chip--warning.active { background: #c25a02; border-color: #c25a02; }
        .sup-severity-chip--info.active { background: #002045; border-color: #002045; }
        .sup-page__filter-row { display: flex; align-items: flex-end; gap: 1rem; flex-wrap: wrap; }
        .sup-page__filter-field { min-width: 220px; }
        .sup-page__filter-field--grow { flex: 1; min-width: 260px; }
        .sup-page__loading { display: flex; flex-direction: column; gap: 0.5rem; padding: 1.5rem; color: #43474e; }
        .sup-page__loading-text { font-size: 0.875rem; }
        .sup-page__error { padding: 3rem 2rem; text-align: center; color: #ba1a1a; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .sup-page__error-title { font-weight: 600; margin: 0; }
        .sup-page__error-detail { font-size: 0.875rem; margin: 0; color: #74777f; }
        .sup-page__empty { padding: 3rem 2rem; text-align: center; color: #74777f; }
        .sup-page__empty-text { margin: 0 0 1rem; font-size: 0.875rem; }
        .sup-page__table-wrap { overflow-x: auto; }
        .sup-page__table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .sup-page__table th { text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .sup-page__table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .sup-row:hover td { background: #eceef0; }
        .sup-cell--number { font-weight: 600; white-space: nowrap; font-variant-numeric: tabular-nums; font-family: "JetBrains Mono", ui-monospace, monospace;}
        .sup-cell--subject { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .sup-cell--date { white-space: nowrap; }
        .sup-cell--est { color: #c25a02; font-weight: 700; }
        .sup-cell--actions { white-space: nowrap; }
        .sup-actions { display: flex; gap: 0.25rem; align-items: center; }
        .sup-page__footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap; padding: 1rem; border-top: 1px solid #eceef0; }
        .sup-page__footer-text { font-size: 0.8125rem; color: #43474e; }
        .sup-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 420px; }
        .sup-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .sup-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .sup-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
        .sup-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 1rem; }
        .sup-detail-label { margin: 0; font-size: 0.75rem; color: #74777f; }
        .sup-detail-value { margin: 0.125rem 0 0; font-weight: 600; font-size: 0.875rem; word-break: break-word; }
        .sup-section-title { margin: 1rem 0 0.5rem; font-size: 1.25rem; line-height: 1.75rem; font-weight: 600; color: #191c1e; }
        .sup-timeline { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0; }
        .sup-timeline-item { position: relative; padding: 0 0 1rem 1.5rem; border-left: 2px solid #e0e3e5; margin-left: 0.375rem; }
        .sup-timeline-item:last-child { border-left-color: transparent; padding-bottom: 0.25rem; }
        .sup-timeline-dot { position: absolute; left: -7px; top: 2px; width: 12px; height: 12px; border-radius: 9999px; background: #002045; border: 2px solid #ffffff; box-shadow: 0 0 0 1px #002045; }
        .sup-timeline-dot--origin { background: #43474e; box-shadow: 0 0 0 1px #43474e; }
        .sup-timeline-meta { font-size: 0.75rem; color: #74777f; margin: 0 0 0.25rem; }
        .sup-timeline-body { font-size: 0.875rem; color: #191c1e; margin: 0; white-space: pre-wrap; word-break: break-word; }
        .sup-form { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.75rem; }
        .sup-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; }
        @media (max-width: 640px) {
          .sup-page { padding: 1rem; }
          .sup-detail-grid, .sup-form-row { grid-template-columns: 1fr; }
          .sup-cell--subject { max-width: 160px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sup-row td, .sup-severity-chip, .sup-toast { transition: none; }
        }
      `}</style>
    </div>
  );
}

/* ── Drawer: detalhe + timeline + responder/fechar/CSAT ── */

function TicketDetailDrawer({
  tenantId,
  ticketId,
  onClose,
  onToast,
}: {
  tenantId: string;
  ticketId: string | null;
  onClose: () => void;
  onToast: (message: string, variant: 'success' | 'error') => void;
}): JSX.Element {
  const { data: ticket, isLoading, error } = useSupportTicket(tenantId, ticketId ?? '');

  const respondMutation = useRespondTicket(tenantId);
  const closeMutation = useCloseTicket(tenantId);
  const csatMutation = useCsatTicket(tenantId);

  const [message, setMessage] = useState('');
  const [authorType, setAuthorType] = useState<TicketAuthorType>('USER');
  const [closeScore, setCloseScore] = useState('');
  const [csatScore, setCsatScore] = useState('');

  const closed = ticket?.status === 'CLOSED';

  const handleRespond = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!ticketId || !message.trim()) return;
    try {
      await respondMutation.mutateAsync({ id: ticketId, input: { content: message.trim(), authorType } });
      onToast('Resposta registrada na timeline.', 'success');
      setMessage('');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Erro ao responder chamado.', 'error');
    }
  };

  const handleClose = async (): Promise<void> => {
    if (!ticketId) return;
    try {
      await closeMutation.mutateAsync({
        id: ticketId,
        input: closeScore ? { csatScore: parseInt(closeScore, 10) } : {},
      });
      onToast('Chamado fechado.', 'success');
      setCloseScore('');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Erro ao fechar chamado.', 'error');
    }
  };

  const handleCsat = async (): Promise<void> => {
    if (!ticketId || !csatScore) return;
    try {
      await csatMutation.mutateAsync({ id: ticketId, score: parseInt(csatScore, 10) });
      onToast('Avaliação CSAT registrada.', 'success');
      setCsatScore('');
    } catch (err) {
      onToast(err instanceof Error ? err.message : 'Erro ao registrar CSAT.', 'error');
    }
  };

  const footer = (
    <Button variant="outline" onClick={onClose}>
      Fechar
    </Button>
  );

  return (
    <Modal
      isOpen={ticketId !== null}
      onClose={onClose}
      title={ticket ? `Chamado ${ticket.number}` : 'Detalhes do chamado'}
      footer={footer}
      size="lg"
    >
      {isLoading && (
        <div className="sup-page__loading" aria-busy="true" aria-live="polite">
          <LoadingSkeleton width="100%" height="48px" />
          <LoadingSkeleton width="100%" height="120px" />
          <span className="sup-page__loading-text">Carregando detalhes...</span>
        </div>
      )}
      {error && !isLoading && (
        <p role="alert" style={{ color: '#ba1a1a', fontSize: '0.875rem' }}>
          {(error as Error).message}
        </p>
      )}
      {ticket && !isLoading && (
        <div role="document" aria-label={`Detalhes do chamado ${ticket.number}`}>
          <div className="sup-detail-grid">
            <div>
              <p className="sup-detail-label">Assunto</p>
              <p className="sup-detail-value">{ticket.subject}</p>
            </div>
            <div>
              <p className="sup-detail-label">Status</p>
              <p className="sup-detail-value">
                <Badge variant={ticketStatusMeta(ticket.status).variant} size="sm" dot>
                  {ticketStatusMeta(ticket.status).label}
                </Badge>
              </p>
            </div>
            <div>
              <p className="sup-detail-label">Severidade</p>
              <p className="sup-detail-value">
                <Badge variant={ticketSeverityMeta(ticket.severity).variant} size="sm" dot>
                  {ticketSeverityMeta(ticket.severity).label}
                </Badge>
              </p>
            </div>
            <div>
              <p className="sup-detail-label">SLA limite</p>
              <p
                className="sup-detail-value"
                title={ticketSlaLimit(ticket).estimated ? 'Estimado (createdAt + SLA da severidade)' : 'Informado pelo servidor'}
              >
                {formatDateTime(ticketSlaLimit(ticket).limit?.toISOString() ?? null)}
                {ticketSlaLimit(ticket).estimated ? ' *' : ''}
              </p>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <p className="sup-detail-label">Descrição</p>
              <p className="sup-detail-value" style={{ fontWeight: 400 }}>
                {ticket.description ?? '—'}
              </p>
            </div>
            <div>
              <p className="sup-detail-label">Responsável</p>
              <p className="sup-detail-value">{ticket.assignedTo ?? '—'}</p>
            </div>
            <div>
              <p className="sup-detail-label">CSAT</p>
              <p className="sup-detail-value">{ticket.csatScore !== null ? `${ticket.csatScore} / 5` : '—'}</p>
            </div>
          </div>

          <h3 className="sup-section-title">Timeline ({ticket.messages.length} interações)</h3>
          {ticket.messages.length === 0 ? (
            <p style={{ fontSize: '0.875rem', color: '#74777f' }}>
              Nenhuma interação registrada. Use o formulário abaixo para responder.
            </p>
          ) : (
            <ol className="sup-timeline">
              {ticket.messages.map((m) => (
                <li key={m.id} className="sup-timeline-item">
                  <span className="sup-timeline-dot" aria-hidden="true" />
                  <p className="sup-timeline-meta">
                    {m.authorType === 'AGENT' ? 'Atendente' : m.authorType === 'SYSTEM' ? 'Sistema' : 'Usuário'}
                    {' · '}
                    {formatDateTime(m.createdAt)}
                  </p>
                  <p className="sup-timeline-body">{m.content}</p>
                </li>
              ))}
            </ol>
          )}

          {!closed && (
            <form className="sup-form" onSubmit={(e) => void handleRespond(e)}>
              <h3 className="sup-section-title">Responder (POST :id/respond)</h3>
              <Textarea
                label="Mensagem"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={3}
                placeholder="Escreva a resposta ao solicitante..."
              />
              <div className="sup-form-row">
                <Select
                  label="Autor"
                  value={authorType}
                  onChange={(e) => setAuthorType(e.target.value as TicketAuthorType)}
                >
                  <option value="USER">Usuário</option>
                  <option value="AGENT">Atendente</option>
                </Select>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button variant="primary" type="submit" loading={respondMutation.isPending} disabled={!message.trim()}>
                    Enviar resposta
                  </Button>
                </div>
              </div>
            </form>
          )}

          <div className="sup-form">
            <h3 className="sup-section-title">Encerrar / avaliar</h3>
            <div className="sup-form-row">
              <Select
                label="CSAT ao fechar (opcional)"
                value={closeScore}
                onChange={(e) => setCloseScore(e.target.value)}
                disabled={closed}
              >
                <option value="">Sem avaliação</option>
                <option value="1">1 — Muito insatisfeito</option>
                <option value="2">2 — Insatisfeito</option>
                <option value="3">3 — Neutro</option>
                <option value="4">4 — Satisfeito</option>
                <option value="5">5 — Muito satisfeito</option>
              </Select>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="outline" type="button" loading={closeMutation.isPending} disabled={closed} onClick={() => void handleClose()}>
                  Fechar chamado
                </Button>
              </div>
            </div>
            <div className="sup-form-row">
              <Select label="Avaliação CSAT (POST :id/csat)" value={csatScore} onChange={(e) => setCsatScore(e.target.value)}>
                <option value="">Selecione 1–5</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5">5</option>
              </Select>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button variant="outline" type="button" loading={csatMutation.isPending} disabled={!csatScore} onClick={() => void handleCsat()}>
                  Registrar CSAT
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/* ── Modal criar (contrato POST /support/tickets) ── */

function CreateTicketModal({
  isOpen,
  onClose,
  onSubmit,
  submitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (input: CreateTicketInput) => Promise<void>;
  submitting: boolean;
}): JSX.Element {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<TicketSeverity>('MEDIUM');
  const [companyId, setCompanyId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const reset = useCallback(() => {
    setSubject('');
    setDescription('');
    setSeverity('MEDIUM');
    setCompanyId('');
    setAssignedTo('');
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!subject.trim()) return;
    await onSubmit({
      subject: subject.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      severity,
      ...(companyId.trim() ? { companyId: companyId.trim() } : {}),
      ...(assignedTo.trim() ? { assignedTo: assignedTo.trim() } : {}),
    });
    reset();
  };

  const footer = (
    <>
      <Button
        variant="outline"
        type="button"
        onClick={() => {
          reset();
          onClose();
        }}
      >
        Cancelar
      </Button>
      <Button variant="primary" type="submit" form="create-ticket-form" loading={submitting} disabled={!subject.trim()}>
        Abrir chamado
      </Button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Novo Chamado de Suporte" footer={footer}>
      <form id="create-ticket-form" onSubmit={(e) => void handleSubmit(e)}>
        <div className="sup-form" style={{ marginTop: 0 }}>
          <Input
            label="Assunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            placeholder="Resumo do problema..."
          />
          <Textarea
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            placeholder="Detalhe o problema, passos e impacto..."
          />
          <div className="sup-form-row">
            <Select label="Severidade" value={severity} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSeverity(e.target.value as TicketSeverity)}>
              <option value="LOW">Baixa (SLA 72h)</option>
              <option value="MEDIUM">Média (SLA 24h)</option>
              <option value="HIGH">Alta (SLA 8h)</option>
              <option value="CRITICAL">Crítica (SLA 2h)</option>
            </Select>
            <Input
              label="Empresa (opcional)"
              value={companyId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyId(e.target.value)}
              placeholder="ID da empresa"
            />
          </div>
          <Input
            label="Responsável (opcional)"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            placeholder="ID do responsável"
          />
        </div>
      </form>
    </Modal>
  );
}
